/**
 * Empty / error placeholder for chart tiles.
 *
 * Default recharts behaviour for an empty data array is to render the
 * axes alone — a half-formed chart frame the user has to interpret as
 * "broken or just no data?". Tile error paths previously rendered
 * either the same empty frame or the chart's normal layout with no
 * series. This component replaces both with a clean, intentional
 * placeholder so the user knows immediately what they're looking at.
 *
 * Two visual variants:
 *   - empty: muted, AlertCircle, "No data" + optional reason
 *   - error: rose-tinted, TriangleAlert, "Query failed" + message
 *
 * Intentionally minimal: no actions, no retry button. The dashboard
 * toolbar already exposes refresh; this component is a status surface,
 * not a control surface.
 */

import { AlertCircle, TriangleAlert } from "@/components/icons"

import { cn } from "@/lib/utils"

export type TileEmptyVariant = "empty" | "error"

export interface TileEmptyStateProps {
  variant?: TileEmptyVariant
  /**
   * Tile's title (rendered as a small heading above the placeholder
   * so context isn't lost). Optional — when absent we just show the
   * status block.
   */
  title?: string
  /** "Query returned 0 rows", "Connection unavailable", etc. */
  reason?: string
  /** Match the regular tile chrome so the layout doesn't shift. */
  height?: number
}

const TITLE_TEXT: Record<TileEmptyVariant, string> = {
  empty: "Inga data",
  error: "Frågan misslyckades",
}

export function TileEmptyState({
  variant = "empty",
  title,
  reason,
  height = 280,
}: TileEmptyStateProps) {
  const isError = variant === "error"
  const Icon = isError ? TriangleAlert : AlertCircle
  return (
    <div className="bg-card border-border rounded-xl border p-4 shadow-sm">
      {title && <h3 className="text-foreground mb-2 text-sm font-medium">{title}</h3>}
      <div
        className={cn(
          "flex flex-col items-center justify-center rounded-md text-center",
          isError
            ? "border-destructive bg-destructive/15 text-foreground border"
            : "border-border/50 bg-muted/20 text-muted-foreground border-dashed"
        )}
        style={{ minHeight: height }}
      >
        <Icon
          aria-hidden="true"
          className={cn(
            "mb-2 size-8 opacity-70",
            isError ? "text-destructive" : "text-muted-foreground"
          )}
        />
        <div className="text-sm font-medium">{TITLE_TEXT[variant]}</div>
        {reason && <div className="mt-1 max-w-xs text-xs opacity-80">{reason}</div>}
      </div>
    </div>
  )
}
